/* eslint-disable */
import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

//Custom
import MapContext from './MapContext';

//Types
import { BoolSettings, StringSettings } from '@/@types/common';
import Map from 'ol/Map';
import { InitOptionsType } from './MapContext';

export type MapContextProviderProps = {
  map: Map | null;
  initOptions: InitOptionsType;
  mapId: number | undefined;
  children: JSX.Element | JSX.Element[];
};

const MapContextProvider: FunctionComponent<MapContextProviderProps> = (props) => {
  const [forceRefreshCounter, setForceRefreshCounter] = useState(0);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [opacityValues, setOpacityValues] = useState<Record<string, number>>({});

  const { map, initOptions, mapId } = props;

  const setLayerVisibility = useCallback((id: string, isVisible: boolean) => {
    const key: string = 'maplayers';
    const item = localStorage.getItem(key);
    const settings = item ? JSON.parse(item) : null;

    if (settings === null || Object.keys(settings).length === 0) {
      localStorage.setItem(key, JSON.stringify({ [id]: isVisible }));
    } else {
      settings[id] = isVisible;
      localStorage.setItem(key, JSON.stringify(settings));
    }

    setForceRefreshCounter((prev) => prev + 1);
  }, []);

  const getLayerVisibility = useCallback(() => {
    const key: string = 'maplayers';

    const item = localStorage.getItem(key);
    const settings = item ? JSON.parse(item) : null;

    return settings ? (settings as BoolSettings) : ({} as BoolSettings);
  }, []);

  const setLayerTime = useCallback((id: string, time: string) => {
    const key = 'layerstime';
    const item = localStorage.getItem(key);
    const settings = item ? JSON.parse(item) : null;

    if (settings === null || Object.keys(settings).length === 0) {
      localStorage.setItem(key, JSON.stringify({ [id]: time }));
    } else {
      settings[id] = time;
      localStorage.setItem(key, JSON.stringify(settings));
    }

    setForceRefreshCounter((prev) => prev + 1);
  }, []);

  const clearLayerVisibility = useCallback(() => {
    const key: string = 'maplayers';
    localStorage.setItem(key, JSON.stringify({}));
    setForceRefreshCounter((prev) => prev + 1);
  }, []);

  const clearLayerOpacity = useCallback(() => {
    const key: string = 'opacity_layer_values';
    localStorage.setItem(key, JSON.stringify({}));
    setForceRefreshCounter((prev) => prev + 1);
  }, [])

  const clearLayerHistory = useCallback(() => {
    const key: string = 'layerstime';
    localStorage.setItem(key, JSON.stringify({}));
    setForceRefreshCounter((prev) => prev + 1);
  }, [])

  const getLayerTime = useCallback(() => {
    const key = 'layerstime';
    const item = localStorage.getItem(key);
    const settings = item ? JSON.parse(item) : null;
    return settings ? (settings as StringSettings) : ({} as StringSettings);
  }, []);

  const layerVisibility = useMemo(() => {
    return getLayerVisibility();
  }, [forceRefreshCounter]);

  const layerTime = useMemo(() => {
    return getLayerTime();
  }, [forceRefreshCounter]);

  const stableInitOptions = useMemo(() => initOptions, [initOptions]);

  const providerValue = useMemo(
    () => ({
      map,
      mapId,
      initOptions,
      setLayerVisibility,
      layerVisibility,
      setLayerTime,
      layerTime,
      forceRefreshCounter,
      sidebarCollapsed,
      setSidebarCollapsed,
      clearLayerVisibility,
      clearLayerHistory,
      clearLayerOpacity,
      opacityValues,
      setOpacityValues,
    }),
    [map, mapId, initOptions, setLayerVisibility, layerVisibility, setLayerTime, layerTime, forceRefreshCounter, sidebarCollapsed, clearLayerVisibility, clearLayerHistory, clearLayerOpacity, opacityValues, setOpacityValues]
  );

  return <MapContext.Provider value={providerValue}>{props.children}</MapContext.Provider>;
};

export default MapContextProvider;
